/* eslint-disable react/jsx-one-expression-per-line */
import { useHookstate } from '@hookstate/core'
import React, { useEffect, useState } from 'react'
import Layout from '~/components/Layout'
import { useLang } from '~/hooks/useLang'
import {
  Grid,
  Button,
  FormControl,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material'
import InternalContent from '~/components/Layout/components/InternalContent'
import ClearIcon from '@mui/icons-material/Clear'
import FilterAutocompleteDevice from '~/components/FilterAutocompleteDevice'
import FilterAutocompleteFetch from '~/components/FilterAutocompleteFetch'
import { getWorkSchedulesByUser, getWorkSchedulesByDevice } from '../services'
import notify from '~/utils/notify'
import { useHistory } from 'react-router-dom'
import InternalHeader from '~/components/Layout/components/InternalHeader'
// no types required since results are not rendered here

export default function Index() {
  const { translate } = useLang()

  const [searchType, setSearchType] = useState<'user' | 'device'>('user')
  const userId = useHookstate<number | null>(null)
  const deviceId = useHookstate<number | null>(null)
  // results are intentionally not stored in this page (printing handled elsewhere)
  const isLoading = useHookstate<boolean>(false)
  const history = useHistory()

  useEffect(() => {
    document.title = translate('Resources - Dispatch System')
  }, [])

  async function handleSearch() {
    isLoading.set(true)
    try {
      const uid = userId.get()
      const did = deviceId.get()

      if (searchType === 'user' && uid != null) {
        const data = await getWorkSchedulesByUser({ userId: uid })
        if (!data || (Array.isArray(data) && data.length === 0)) {
          notify({
            message: translate('No work schedules for this user'),
            type: 'info',
          })
          return
        }
        // Armazena os dados no sessionStorage
        sessionStorage.setItem(
          'workSchedulePrintData',
          JSON.stringify({ items: data, searchType: 'user', userId: uid })
        )
        // Abre em nova aba
        window.open('/workSchedule-Print/summary', '_blank')
      } else if (searchType === 'device' && did != null) {
        const data = await getWorkSchedulesByDevice({ deviceId: did })
        if (!data || (Array.isArray(data) && data.length === 0)) {
          notify({
            message: translate('No work schedules for this device'),
            type: 'info',
          })
          return
        }
        // Armazena os dados no sessionStorage
        sessionStorage.setItem(
          'workSchedulePrintData',
          JSON.stringify({ items: data, searchType: 'device', deviceId: did })
        )
        // Abre em nova aba
        window.open('/workSchedule-Print/summary', '_blank')
      } else {
        // no-op
      }
    } finally {
      isLoading.set(false)
    }
  }

  // results are kept in state for possible later use, but not displayed on this page

  return (
    <Layout marginLayout={true}>
      <InternalHeader title={`${translate('Created scales')}`} />

      <InternalContent
        title={`${translate('Filters')}`}
        sxFont={{ fontWeight: 'bold', fontSize: 18 }}
      >
        <Grid container xs={12} spacing={2} alignItems={'center'}>
          <Grid
            item
            xs={3}
            sx={{ pr: 1, display: 'flex', alignItems: 'center' }}
          >
            <FormControl fullWidth size="small">
              {/* <InputLabel id="search-type-label" shrink={searchType !== null}>{translate('Search Type')}</InputLabel> */}
              <Select
                size="small"
                labelId="search-type-label"
                id="search-type"
                value={searchType}
                displayEmpty
                notched={searchType !== null}
                onChange={(event) =>
                  setSearchType(event.target.value as 'user' | 'device')
                }
                endAdornment={
                  searchType ? (
                    <IconButton
                      size="small"
                      onClick={() => setSearchType('user')}
                      sx={{ mr: 1 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ) : null
                }
              >
                <MenuItem value={'user'}>{translate('By user')}</MenuItem>
                <MenuItem value={'device'}>{translate('By device')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
            {searchType === 'user' ? (
              <FilterAutocompleteFetch
                label={translate('User')}
                urlFetch={'/user/get'}
                dataRequest={'user'}
                size={'small'}
                onFetchChange={(selectedUser) => userId.set(selectedUser)}
                valueId={userId.get() ?? undefined}
              />
            ) : (
              <FilterAutocompleteDevice
                deviceId={deviceId.get()}
                onDeviceChange={(selectedDevice) =>
                  deviceId.set(selectedDevice)
                }
                disabled={false}
              />
            )}
          </Grid>

          <Grid
            item
            xs={5}
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                userId.set(null)
                deviceId.set(null)
              }}
            >
              {translate('Clear')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={isLoading.get()}
            >
              {translate('Print')}
            </Button>
          </Grid>
        </Grid>
      </InternalContent>

      {/* Results are not shown on this page per request - only search functionality kept */}
    </Layout>
  )
}
